import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Check, Sparkles } from "lucide-react";

export const INCLUSIONS = [
  ["blankets", "Blankets & wrapping"],
  ["disassembly", "Furniture disassembly & reassembly"],
  ["insurance", "Insurance"],
  ["fuel_tolls", "Fuel & tolls"],
  ["stairs", "Stairs"],
];

export function BidExtrasEditor({ inclusions, setInclusions, extras, setExtras }) {
  const toggle = (k) => setInclusions((v) => ({ ...v, items: v.items.includes(k) ? v.items.filter((x) => x !== k) : [...v.items, k] }));
  const setExtra = (i, patch) => setExtras((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="space-y-4">
      <div className="space-y-2" data-testid="bid-inclusions">
        <Label>What's included in your price</Label>
        <div className="grid sm:grid-cols-2 gap-2">
          {INCLUSIONS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm rounded-lg border border-border px-3 py-2 cursor-pointer hover:border-kinetic/50">
              <Checkbox checked={inclusions.items.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`bid-inc-${k}`} /> {label}
            </label>
          ))}
        </div>
        <Input value={inclusions.other} maxLength={300} onChange={(e) => setInclusions((v) => ({ ...v, other: e.target.value }))}
          placeholder="Other inclusions (e.g. 2 movers + 6t truck, mattress bags)" data-testid="bid-inc-other" />
      </div>
      <div className="space-y-2" data-testid="bid-extras">
        <Label>Additional services (optional extras)</Label>
        {extras.map((x, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={x.name} maxLength={80} onChange={(e) => setExtra(i, { name: e.target.value })} placeholder="e.g. Packing service" data-testid={`bid-extra-name-${i}`} />
            <Input type="number" min="0" value={x.price} onChange={(e) => setExtra(i, { price: e.target.value })} placeholder="$" className="w-28" data-testid={`bid-extra-price-${i}`} />
            <button type="button" onClick={() => setExtras((xs) => xs.filter((_, j) => j !== i))} className="h-9 w-9 shrink-0 rounded-md text-muted-foreground hover:text-destructive flex items-center justify-center" data-testid={`bid-extra-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {extras.length < 10 && (
          <Button type="button" size="sm" variant="outline" onClick={() => setExtras((xs) => [...xs, { name: "", price: "" }])} className="gap-1" data-testid="bid-extra-add-btn"><Plus className="h-4 w-4" /> Add a service</Button>
        )}
      </div>
    </div>
  );
}

export function BidExtrasView({ bid }) {
  const items = bid.inclusions?.items || [];
  const other = bid.inclusions?.other;
  const extras = bid.extras || [];
  if (!items.length && !other && !extras.length) return null;
  const labels = Object.fromEntries(INCLUSIONS);
  return (
    <div className="space-y-2 mb-3" data-testid={`bid-extras-view-${bid.id}`}>
      {(items.length > 0 || other) && (
        <div>
          <p className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Included in price</p>
          <div className="flex flex-wrap gap-1.5">
            {items.map((k) => <span key={k} className="inline-flex items-center gap-1 text-xs rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5"><Check className="h-3 w-3" />{labels[k]}</span>)}
          </div>
          {other && <p className="text-xs text-muted-foreground mt-1">{other}</p>}
        </div>
      )}
      {extras.length > 0 && (
        <div>
          <p className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Optional extras</p>
          <ul className="space-y-0.5">
            {extras.map((x, i) => (
              <li key={i} className="text-sm flex items-center justify-between gap-2"><span className="flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-kinetic" />{x.name}</span><span className="font-semibold">+${x.price}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
